/**
 * Servicio para manejar el perfil del usuario en Firebase Realtime Database
 */

import { firebase } from '../config/firebase.config';
import { Storage } from '@capacitor/storage';
import ProfilePhotoService from './ProfilePhotoService';

export interface UserProfile {
  ced: string;
  username: string;
  email?: string;
  phone?: string;
  company?: string;
  position?: string;
  eventcode: string;
  profilePhotoUrl?: string;
  profilePhotoStorageUrl?: string;
  profilePhotoStoragePending?: boolean;
  updatedAt?: any;
}

class UserProfileService {
  private static instance: UserProfileService;
  private profilePhotoService: ProfilePhotoService;
  private profilePhotoBackupsInProgress = new Set<string>();
  private profilePhotoBackupListeners = new Map<string, () => void>();

  constructor() {
    this.profilePhotoService = ProfilePhotoService.getInstance();
  }

  static getInstance(): UserProfileService {
    if (!UserProfileService.instance) {
      UserProfileService.instance = new UserProfileService();
    }
    return UserProfileService.instance;
  }

  /**
   * Obtener el perfil del usuario actual desde Firebase
   */
  async getCurrentUserProfile(
    userced: string,
    eventcode: string,
    retryPendingPhotoBackup: boolean = true
  ): Promise<UserProfile | null> {
    try {
      console.log('UserProfileService: Obteniendo perfil para:', { userced, eventcode });

      // Buscar en la estructura /user de Firebase
      const usersSnapshot = await firebase.database().ref('/user').once('value');
      
      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        
        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === userced && userData.eventcode === eventcode) {
            // `userphoto` es el campo histórico consumido por el login legacy.
            // `profilePhotoUrl` fue introducido transitoriamente por la app.
            // Si existe el campo nuevo, migrarlo sin mantener dos copias Base64.
            const transitionalPhoto = typeof userData.profilePhotoUrl === 'string'
              ? userData.profilePhotoUrl
              : '';
            const legacyPhoto = typeof userData.userphoto === 'string'
              ? userData.userphoto
              : '';
            const resolvedProfilePhoto = transitionalPhoto || legacyPhoto;

            if (transitionalPhoto && transitionalPhoto !== legacyPhoto) {
              try {
                await firebase.database().ref(`/user/${userId}`).update({
                  userphoto: transitionalPhoto,
                  profilePhotoUrl: null
                });
                console.log('UserProfileService: Foto migrada al campo legacy userphoto');
              } catch (migrationError) {
                console.warn('UserProfileService: No se pudo migrar la foto a userphoto:', migrationError);
              }
            }

            console.log('UserProfileService: Perfil encontrado:', {
              ced: userData.ced,
              eventcode: userData.eventcode,
              hasProfilePhoto: Boolean(resolvedProfilePhoto),
              hasStorageBackup: Boolean(userData.profilePhotoStorageUrl),
              storageBackupPending: userData.profilePhotoStoragePending === true
            });
            const profile = {
              ced: userData.ced,
              username: userData.username || '',
              email: userData.email || '',
              phone: userData.phone || '',
              company: userData.company || '',
              position: userData.role || userData.position || '',
              eventcode: userData.eventcode,
              profilePhotoUrl: resolvedProfilePhoto,
              profilePhotoStorageUrl: userData.profilePhotoStorageUrl || '',
              profilePhotoStoragePending: userData.profilePhotoStoragePending === true,
              updatedAt: userData.updatedAt
            };

            if (
              retryPendingPhotoBackup &&
              profile.profilePhotoStoragePending &&
              this.isBase64ProfilePhoto(profile.profilePhotoUrl)
            ) {
              this.scheduleProfilePhotoBackup(
                userced,
                eventcode,
                profile.profilePhotoUrl,
                profile.profilePhotoStorageUrl
              );
            }

            return profile;
          }
        }
      }

      console.log('UserProfileService: No se encontró perfil para el usuario');
      return null;
    } catch (error) {
      console.error('UserProfileService: Error al obtener perfil:', error);
      return null;
    }
  }

  /**
   * Actualizar el perfil del usuario en Firebase
   */
  async updateUserProfile(userced: string, eventcode: string, updates: Partial<UserProfile>): Promise<boolean> {
    try {
      console.log('UserProfileService: Actualizando perfil:', {
        userced,
        eventcode,
        fields: Object.keys(updates)
      });

      // Buscar el usuario en Firebase para obtener su ID
      const usersSnapshot = await firebase.database().ref('/user').once('value');
      
      if (usersSnapshot.exists()) {
        const allUsers = usersSnapshot.val();
        
        for (const userId in allUsers) {
          const userData = allUsers[userId];
          if (userData.ced === userced && userData.eventcode === eventcode) {
            // `userphoto` continúa siendo el campo canónico para compatibilidad
            // legacy. `profilePhotoUrl` se elimina para no duplicar el Base64.
            const { position, profilePhotoUrl, ...editableUpdates } = updates;
            const updateData: Record<string, any> = {
              ...editableUpdates,
              updatedAt: firebase.database.ServerValue.TIMESTAMP
            };

            if (profilePhotoUrl !== undefined) {
              updateData.userphoto = profilePhotoUrl;
              updateData.profilePhotoUrl = null;
            }

            await firebase.database().ref(`/user/${userId}`).update(updateData);
            
            console.log('UserProfileService: Perfil actualizado exitosamente');
            
            // También actualizar el perfil de networking si existe
            await this.updateNetworkingProfile(userced, eventcode, updates);
            
            return true;
          }
        }
      }

      console.log('UserProfileService: Usuario no encontrado para actualizar');
      return false;
    } catch (error) {
      console.error('UserProfileService: Error al actualizar perfil:', error);
      return false;
    }
  }

  /**
   * Actualizar también el perfil de networking para mantener consistencia
   */
  private async updateNetworkingProfile(userced: string, eventcode: string, updates: Partial<UserProfile>): Promise<void> {
    try {
      const networkingRef = firebase.database().ref(`networking_profiles/${eventcode}/${userced}`);
      
      // Verificar si existe el perfil de networking
      const snapshot = await networkingRef.once('value');
      
      if (snapshot.exists()) {
        const networkingUpdates: any = {};
        
        // Mapear los campos del perfil a los campos de networking
        if (updates.username) {
          networkingUpdates.name = updates.username;
        }

        if (updates.email) networkingUpdates.email = updates.email;
        if (updates.phone) networkingUpdates.phone = updates.phone;
        if (updates.company) networkingUpdates.company = updates.company;
        if (updates.profilePhotoUrl !== undefined) {
          networkingUpdates.profilePhotoUrl = updates.profilePhotoUrl;
        }
        if (updates.profilePhotoStorageUrl !== undefined) {
          networkingUpdates.profilePhotoStorageUrl = updates.profilePhotoStorageUrl;
        }
        if (updates.profilePhotoStoragePending !== undefined) {
          networkingUpdates.profilePhotoStoragePending = updates.profilePhotoStoragePending;
        }
        // position no se actualiza porque no es editable por el usuario

        networkingUpdates.updatedAt = firebase.database.ServerValue.TIMESTAMP;
        
        await networkingRef.update(networkingUpdates);
        console.log('UserProfileService: Perfil de networking actualizado');
      }
    } catch (error) {
      console.warn('UserProfileService: Error al actualizar perfil de networking:', error);
    }
  }

  /**
   * Crear un nuevo perfil de usuario en Firebase
   */
  async createUserProfile(profile: UserProfile): Promise<boolean> {
    try {
      console.log('UserProfileService: Creando nuevo perfil:', profile);

      const newUserRef = await firebase.database().ref('/user').push({
        ...profile,
        createdAt: firebase.database.ServerValue.TIMESTAMP,
        updatedAt: firebase.database.ServerValue.TIMESTAMP
      });

      if (newUserRef.key) {
        console.log('UserProfileService: Perfil creado con ID:', newUserRef.key);
        return true;
      }

      return false;
    } catch (error) {
      console.error('UserProfileService: Error al crear perfil:', error);
      return false;
    }
  }

  /**
   * Guardar perfil en storage local para cache
   */
  async saveProfileToStorage(profile: UserProfile): Promise<void> {
    try {
      await Storage.set({
        key: 'user_profile',
        value: JSON.stringify(profile)
      });
      console.log('UserProfileService: Perfil guardado en storage local');
    } catch (error) {
      console.error('UserProfileService: Error al guardar en storage:', error);
    }
  }

  /**
   * Obtener perfil desde storage local
   */
  async getProfileFromStorage(): Promise<UserProfile | null> {
    try {
      const result = await Storage.get({ key: 'user_profile' });
      if (result.value) {
        return JSON.parse(result.value);
      }
      return null;
    } catch (error) {
      console.error('UserProfileService: Error al obtener desde storage:', error);
      return null;
    }
  }

  /**
   * Actualizar foto de perfil del usuario
   */
  async updateProfilePhoto(userced: string, eventcode: string, photoUrl: string): Promise<string | null> {
    try {
      console.log('UserProfileService: Actualizando foto de perfil:', { userced, eventcode });

      const currentProfile = await this.getCurrentUserProfile(userced, eventcode, false);
      const oldStorageUrl = currentProfile?.profilePhotoStorageUrl || (
        currentProfile?.profilePhotoUrl &&
        this.profilePhotoService.isFirebaseStorageUrl(currentProfile.profilePhotoUrl)
          ? currentProfile.profilePhotoUrl
          : ''
      );

      // El Base64 es la copia principal y de consulta directa en Realtime Database.
      const success = await this.updateUserProfile(userced, eventcode, {
        profilePhotoUrl: photoUrl,
        profilePhotoStoragePending: true
      });

      if (!success) return null;

      // Storage es un respaldo: su fallo nunca revierte la copia Base64 principal.
      if (firebase.auth().currentUser) {
        const backupSaved = await this.persistProfilePhotoBackup(
          userced,
          eventcode,
          photoUrl,
          oldStorageUrl
        );
        if (!backupSaved) {
          this.scheduleProfilePhotoBackup(userced, eventcode, photoUrl, oldStorageUrl);
        }
      } else {
        this.scheduleProfilePhotoBackup(userced, eventcode, photoUrl, oldStorageUrl);
      }

      return photoUrl;
    } catch (error) {
      console.error('UserProfileService: Error al actualizar foto de perfil:', error);
      return null;
    }
  }

  /**
   * Obtener URL de foto de perfil del usuario
   */
  async getProfilePhotoUrl(userced: string, eventcode: string): Promise<string> {
    try {
      const profile = await this.getCurrentUserProfile(userced, eventcode);
      return profile?.profilePhotoUrl || this.profilePhotoService.getDefaultProfilePhoto();
    } catch (error) {
      console.error('UserProfileService: Error al obtener foto de perfil:', error);
      return this.profilePhotoService.getDefaultProfilePhoto();
    }
  }

  /**
   * Eliminar foto de perfil del usuario
   */
  async removeProfilePhoto(userced: string, eventcode: string): Promise<boolean> {
    try {
      console.log('UserProfileService: Eliminando foto de perfil:', { userced, eventcode });

      // Obtener la URL de la foto actual
      const currentProfile = await this.getCurrentUserProfile(userced, eventcode, false);
      const storagePhotoUrl = currentProfile?.profilePhotoStorageUrl || (
        currentProfile?.profilePhotoUrl &&
        this.profilePhotoService.isFirebaseStorageUrl(currentProfile.profilePhotoUrl)
          ? currentProfile.profilePhotoUrl
          : ''
      );

      // Actualizar el perfil para remover la foto
      const success = await this.updateUserProfile(userced, eventcode, {
        profilePhotoUrl: '',
        profilePhotoStorageUrl: '',
        profilePhotoStoragePending: false
      });

      if (success && storagePhotoUrl) {
        // Eliminar la foto de Firebase Storage
        await this.profilePhotoService.deleteOldPhoto(storagePhotoUrl);
      }

      return success;
    } catch (error) {
      console.error('UserProfileService: Error al eliminar foto de perfil:', error);
      return false;
    }
  }

  private isBase64ProfilePhoto(photo: string): boolean {
    return /^data:image\/(jpeg|jpg|png|webp);base64,/i.test(photo);
  }

  private getProfilePhotoBackupKey(userced: string, eventcode: string): string {
    return `${eventcode}:${userced}`;
  }

  private async persistProfilePhotoBackup(
    userced: string,
    eventcode: string,
    base64Photo: string,
    oldStorageUrl: string = ''
  ): Promise<boolean> {
    const backupKey = this.getProfilePhotoBackupKey(userced, eventcode);
    if (this.profilePhotoBackupsInProgress.has(backupKey)) return false;

    this.profilePhotoBackupsInProgress.add(backupKey);
    try {
      const storageUrl = await this.profilePhotoService.uploadProfilePhoto(
        base64Photo,
        userced,
        eventcode
      );
      const metadataSaved = await this.updateUserProfile(userced, eventcode, {
        profilePhotoStorageUrl: storageUrl,
        profilePhotoStoragePending: false
      });

      if (!metadataSaved) {
        await this.profilePhotoService.deleteOldPhoto(storageUrl);
        return false;
      }

      if (oldStorageUrl && oldStorageUrl !== storageUrl) {
        await this.profilePhotoService.deleteOldPhoto(oldStorageUrl);
      }
      return true;
    } catch (error) {
      console.warn('UserProfileService: Respaldo de foto pendiente en Firebase Storage.', error);
      return false;
    } finally {
      this.profilePhotoBackupsInProgress.delete(backupKey);
    }
  }

  private scheduleProfilePhotoBackup(
    userced: string,
    eventcode: string,
    base64Photo: string,
    oldStorageUrl: string = ''
  ): void {
    if (!this.isBase64ProfilePhoto(base64Photo)) return;

    const backupKey = this.getProfilePhotoBackupKey(userced, eventcode);
    if (this.profilePhotoBackupListeners.has(backupKey)) return;

    let unsubscribe = () => {};
    unsubscribe = firebase.auth().onAuthStateChanged((authenticatedUser) => {
      if (!authenticatedUser) return;

      unsubscribe();
      this.profilePhotoBackupListeners.delete(backupKey);
      void this.persistProfilePhotoBackup(
        userced,
        eventcode,
        base64Photo,
        oldStorageUrl
      );
    });
    this.profilePhotoBackupListeners.set(backupKey, unsubscribe);
  }
}

export default UserProfileService;
